import { get, getStatic, set } from '../../../../../services/local-storage';
import type { WeatherForecastCardName } from '../analytics/weatherForecastAnalytics';

export const WEATHER_FORECAST_CARD_STORAGE_NAMESPACE = 'weatherForecast';
export const WEATHER_FORECAST_CARD_EXPANDED_STORAGE_KEY = 'cardExpanded';

export type WeatherForecastCardExpandedState = Partial<
  Record<WeatherForecastCardName, boolean>
>;

type Listener = () => void;

const listeners = new Set<Listener>();

/**
 * In-session overrides so every mounted day-swipe pane shares expand/collapse
 * immediately (localStorage alone does not notify sibling React trees).
 */
let sessionOverrides: WeatherForecastCardExpandedState = {};

const isCardExpandedState = (
  value: unknown,
): value is WeatherForecastCardExpandedState =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const notifyWeatherForecastCardExpandedListeners = (): void => {
  listeners.forEach((listener) => listener());
};

export const subscribeWeatherForecastCardExpanded = (
  listener: Listener,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const readWeatherForecastCardExpandedState =
  (): WeatherForecastCardExpandedState => {
    const stored = getStatic(
      WEATHER_FORECAST_CARD_STORAGE_NAMESPACE,
      WEATHER_FORECAST_CARD_EXPANDED_STORAGE_KEY,
    );

    return isCardExpandedState(stored) ? stored : {};
  };

export const loadWeatherForecastCardExpandedState =
  async (): Promise<WeatherForecastCardExpandedState> => {
    const stored = await get(
      WEATHER_FORECAST_CARD_STORAGE_NAMESPACE,
      WEATHER_FORECAST_CARD_EXPANDED_STORAGE_KEY,
    );

    return isCardExpandedState(stored) ? stored : {};
  };

export const getStoredWeatherForecastCardExpanded = (
  card: WeatherForecastCardName,
  defaultExpanded: boolean,
): boolean => {
  const sessionExpanded = sessionOverrides[card];
  if (typeof sessionExpanded === 'boolean') {
    return sessionExpanded;
  }

  const stored = readWeatherForecastCardExpandedState()[card];
  return typeof stored === 'boolean' ? stored : defaultExpanded;
};

/**
 * Apply async storage into the shared session map without clobbering toggles
 * that already happened this session.
 */
export const mergeHydratedWeatherForecastCardExpandedState = (
  stored: WeatherForecastCardExpandedState,
): void => {
  let changed = false;
  const next: WeatherForecastCardExpandedState = { ...sessionOverrides };

  (Object.entries(stored) as Array<[WeatherForecastCardName, boolean]>).forEach(
    ([card, expanded]) => {
      if (typeof next[card] === 'boolean') {
        return;
      }
      if (typeof expanded !== 'boolean') {
        return;
      }
      next[card] = expanded;
      changed = true;
    },
  );

  if (!changed) {
    return;
  }

  sessionOverrides = next;
  notifyWeatherForecastCardExpandedListeners();
};

export const persistWeatherForecastCardExpanded = async (
  card: WeatherForecastCardName,
  expanded: boolean,
): Promise<void> => {
  sessionOverrides = {
    ...sessionOverrides,
    [card]: expanded,
  };
  notifyWeatherForecastCardExpandedListeners();

  const current = await loadWeatherForecastCardExpandedState();

  await set(
    WEATHER_FORECAST_CARD_STORAGE_NAMESPACE,
    WEATHER_FORECAST_CARD_EXPANDED_STORAGE_KEY,
    {
      ...current,
      [card]: expanded,
    },
    true,
  );
};

/** Test helper — clears in-memory session state between cases. */
export const resetWeatherForecastCardExpandedSessionForTests = (): void => {
  sessionOverrides = {};
  listeners.clear();
};
